/**
 * ================================================================================================
 *  חשבוניות וחיובים · ביטול מנוי — the office's own money screen, in two tabs
 * ================================================================================================
 *
 * The Owner, §11 and §12, 2026-10-30:
 *
 *   *"הוסף טאב חשבוניות וחיובים: טבלה מצומצמת — תאריך, מספר מסמך, תקופת חיוב, סכום כולל מע״מ, מצב,
 *    צפייה/הורדה."*
 *   *"הוסף טאב ביטול מנוי. לא בלחיצה אחת."*
 *
 * ── WHY THIS IS NOT A TAB ON `TenantSubscriptionScreen` ───────────────────────────────────────
 *
 * That screen's own header states its boundary: *"No price, no invoice total, no discount… Money
 * lives in the billing surface; capacity and entitlement live here, and the boundary between them is
 * the thing being protected."* It is 08 §8.4's separation made visible, and putting an invoice total
 * on it would be this round quietly deleting a rule a previous one wrote down.
 *
 * So the two tabs the Owner asked for live together on a screen OF money, linked from the same
 * settings hub, and the subscription screen keeps saying what the office HAS.
 *
 * ── THE TWO TABS ARE GATED SEPARATELY, BY THE SERVER ──────────────────────────────────────────
 *
 * *"צפייה במסמכים, שינוי אמצעי תשלום וביטול הן רשויות נפרדות… באכיפה גם בשרת."* The invoices tab
 * reads `tenant.invoice.read` (`billing.invoice.read`) and the cancellation tab reads and writes
 * `tenant.subscription.cancel.*` (`billing.subscription.cancel`). A person holding one and not the
 * other gets the gate's own refusal inside the tab they cannot use — not a hidden tab, because a
 * tab that vanishes teaches nothing, and `<ServerRead>` renders a denial with the reason.
 *
 * ── THE PRICE IS THE SERVER'S STRING, AND THIS SCREEN DOES NOT FORMAT IT ──────────────────────
 *
 * `amountTotalDisplay` arrives as `117.00 ₪` from `formatAmount` in `domain/money-sum.ts`. This
 * screen prints it. `DataTable`'s own `formatAmount(number)` is deliberately NOT used: it takes a
 * JS number, and a price through a number is a rounded price — the rule that module's header states.
 * One formatter, server-side, is also what stops two clients disagreeing about what is owed.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { FactList } from '../components/FactList';
import { ModuleStorePanel } from '../components/ModuleStorePanel';
import { ChangeRequestPanel } from '../components/ChangeRequestPanel';
import { PaymentMethodPanel } from '../components/PaymentMethodPanel';
import {
  SettlementPanel,
} from '../components/SettlementPanel';
import { NextChargeEstimatePanel } from '../components/NextChargeEstimatePanel';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { TabStrip } from '../components/TabStrip';
import { paymentStageHe } from '../api/billingText';
import { callOperation } from '../api/operations';
import type { CancellationPreviewRead, InvoiceLineRead } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
/*
 * THE ERROR CLASSES, AND NOT A NETWORK PRIMITIVE. `web-client-choke-point` forbids this app naming
 * `fetch`, `XMLHttpRequest`, `WebSocket`, `EventSource` or `sendBeacon` outside `api/operations.ts`;
 * importing the class a refusal is THROWN AS is how four other screens already read a named
 * refusal, and it issues no request.
 *
 * `BadRequestError` IS CAUGHT BEFORE `classifyActionError`, deliberately: that function maps a 400
 * to `{kind:'error'}` with only a reference code, and §12 has SIX named refusals a person is
 * expected to act on — *"הקלד את המילה שוב"* and *"המנוי כבר בוטל"* are different sentences.
 */
import { BadRequestError } from '../api/client';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { useServerData } from '../api/useServerData';
import { useWorkContext } from '../shell/ContextProvider';
import { routes } from '../routes';

/** The word §12 requires, in capitals. Compared exactly — see the service. */
const CONFIRMATION_WORD = 'CANCEL';

/**
 * THE SIX THE SERVICE ANSWERS, EACH MAPPED TO ITS OWN KEY — and the map IS the narrowing.
 *
 * A template key (`t(`billing.refusal.${code}`)`) does not compile: `t` takes a member of the
 * catalogue's literal union, and a computed key would make a missing translation a runtime blank
 * instead of a build failure — which is the property `catalogue.ts`'s own header exists for.
 *
 * So the codes are listed with their keys. A seventh refusal the server grows later is NOT in this
 * record, `namedRefusal` answers `null` for it, and the screen falls through to the uniform
 * wording: *"a refusal nobody has taught a screen about"* is `BadRequestError`'s own rule.
 */
const REFUSAL_KEYS = {
  confirmation_mismatch: 'billing.refusal.confirmation_mismatch',
  reason_required: 'billing.refusal.reason_required',
  unknown_reason: 'billing.refusal.unknown_reason',
  already_cancelled: 'billing.refusal.already_cancelled',
  state_not_cancellable: 'billing.refusal.state_not_cancellable',
  no_subscription: 'billing.refusal.no_subscription',
} as const satisfies Readonly<Record<string, TextKey>>;

const REFUSALS: readonly string[] = Object.keys(REFUSAL_KEYS);

/** The five §12 names, likewise. The server sends the code; the words live here. */
const REASON_KEYS = {
  too_expensive: 'billing.reason.too_expensive',
  missing_capability: 'billing.reason.missing_capability',
  moved_to_another_product: 'billing.reason.moved_to_another_product',
  practice_closing_or_merging: 'billing.reason.practice_closing_or_merging',
  other: 'billing.reason.other',
} as const satisfies Readonly<Record<string, TextKey>>;

type Translate = (key: TextKey) => string;

function refusalText(t: Translate, code: string): string {
  const key = (REFUSAL_KEYS as Readonly<Record<string, TextKey>>)[code];
  /* Unreachable while `namedRefusal` is the only producer, and not worth a crash if it stops being. */
  return key === undefined ? t('billing.cancelRefusedTitle') : t(key);
}

/**
 * A reason code's words, or the CODE ITSELF for one this build has no Hebrew for.
 *
 * The server's `reasons` list is what the radios are built from, so a sixth reason added
 * server-side appears here before this screen is redeployed. Printing the raw code is honest about
 * that; hiding the option would make a reason the server requires unselectable.
 */
function reasonLabel(t: Translate, code: string): string {
  const key = (REASON_KEYS as Readonly<Record<string, TextKey>>)[code];
  return key === undefined ? code : t(key);
}

/**
 * The server's reason code, if it is one this screen has Hebrew for. `null` otherwise.
 *
 * Both error shapes, because the route uses both statuses on purpose: `BadRequestError` carries its
 * body as `unknown` and `ConflictError` carries a typed one, and neither is trusted to hold a code
 * this build can render.
 */
function namedRefusal(err: unknown): string | null {
  const body =
    err instanceof BadRequestError
      ? (err.body as { error?: unknown } | null)
      : classifyConflictBody(err);
  const code = body === null ? undefined : body.error;
  return typeof code === 'string' && REFUSALS.includes(code) ? code : null;
}

function classifyConflictBody(err: unknown): { error?: unknown } | null {
  const failure = classifyActionError(err);
  return failure.kind === 'conflict' ? failure.body : null;
}

/** A cancellation that happened. Held by the SCREEN — see the note where it is declared. */
export interface CancelledOutcome {
  readonly accessEndsOn: string;
  readonly commentTruncated: boolean;
}

/** The two the PANEL keeps, both of which leave the form on screen to be corrected or retried. */
type CancelOutcome =
  | { readonly kind: 'refused'; readonly reason: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function TenantBillingScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const back = { labelHe: t('settings.title'), href: routes.tenantAdmin };

  const readInvoices = useCallback(() => callOperation('tenant.invoice.read', {}), []);
  const invoices = useServerData(readInvoices);
  /*
   * ── THE QUESTION THE TAB IS OPENED TO ANSWER. **2026-11-02.** ──────────────────────
   *
   * The documents were here and the STANDING was not: whether anything is unsettled, and when the
   * next attempt falls. `tenant.statement.read` under `billing.invoice.read` — 08 §12B.3 row 13,
   * *"available in every state, including `suspended`"*, which is the whole reason a customer who
   * cannot pay can still reach this screen.
   *
   * THE SAME SERVICE THE PLATFORM CONSOLE CALLS, so the office and the operator cannot be told
   * different things about the same money.
   */
  const readStatement = useCallback(() => callOperation('tenant.statement.read', {}), []);
  const statement = useServerData(readStatement);

  const readCancellation = useCallback(
    () => callOperation('tenant.subscription.cancel.preview', {}),
    [],
  );
  const cancellation = useServerData(readCancellation);

  /*
   * ══ THE CONFIRMATION LIVES HERE, ABOVE THE READ IT INVALIDATES. 2026-10-30. ════════════
   *
   * MEASURED IN THE RENDERED HARNESS: holding it inside `CancellationPanel` made the success
   * message appear and vanish. The panel calls `reload()` so the preview becomes the server's
   * `alreadyCancelled` answer; `<ServerRead>` goes back to `loading`, UNMOUNTS the panel, and the
   * state that held *"החידוש בוטל"* went with it. The office cancelled its subscription and the
   * screen said nothing.
   *
   * NO SUITE SAW IT. The DOM sweep renders this screen in its loading state and never reaches the
   * panel, and the service tests prove the SERVER cancelled — which it did. *"לאחר הצלחה הצג
   * אישור עם מועד סיום הגישה"* is a requirement about the SCREEN, and only a rendered screen
   * could have shown it was unmet.
   */
  const [cancelled, setCancelled] = useState<CancelledOutcome | null>(null);
  /*
   * ══ THERE IS NO SETTLEMENT OUTCOME TO HOLD ANY MORE. **2026-11-03.** ══════════════════════
   *
   * This screen held one, for a real reason: `SettlementPanel` lives inside a `<ServerRead>` whose
   * read its own press invalidated, so state owned by the panel was destroyed by the refresh that
   * proved the press worked -- the office paid and the screen said nothing.
   *
   * THE PRESS IS GONE, so the state is too. The Owner: *"הלקוח אינו יכול להפעיל חיוב בכרטיס
   * בעצמו... החיוב יחכה לאישור שלי בפלטפורמה."* The office updates its card, which is a different
   * screen's act with its own answer, and the charge is approved on the platform.
   */

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('billing.title')} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('billing.title')}
      backToParent={back}
    >
      <TabStrip
        name="billing"
        ariaLabelHe={t('billing.tabsLabel')}
        tabs={[
          {
            id: 'documents',
            labelHe: t('billing.documentsTab'),
            render: () => (
              <>
                {/*
                  THE STANDING FIRST, THE DOCUMENTS AFTER IT. A person opening this tab while
                  suspended is asking what they owe, and the list of past invoices answers a
                  different question. Its own read, so a failure here does not hide the documents
                  and a failure there does not hide this.
                */}
                <ServerRead
                  result={statement.result}
                  whatHe={t('billing.readingStanding')}
                  skeletonRows={2}
                >
                  {(data) => (
                    <>
                      {/*
                        THE SETTLEMENT PANEL FIRST, AND ONLY WHEN THERE IS SOMETHING TO SETTLE.
                        0046, 2026-11-03.

                        A person opening this tab while restricted is not asking for a fact list --
                        the Owner's second stage has told them *"הסדרה בלבד"* and this is the act.
                        It renders `null` for a paying office, because a permanent panel offering
                        to settle nothing is one people learn to scroll past.

                        INSIDE THE SAME READ as the standing, so the panel's figures and the fact
                        list below it are the same answer rather than two reads that can disagree.
                      */}
                      <SettlementPanel statement={data} />
                      <FactList
                      facts={[
                        {
                          labelHe: t('billing.standing'),
                          value: (
                            <StatusChip
                              labelHe={paymentStageHe(data.payment.stage, data.payment.governed).labelHe}
                              tone={paymentStageHe(data.payment.stage, data.payment.governed).tone}
                            />
                          ),
                        },
                        {
                          labelHe: t('billing.unsettledSince'),
                          value:
                            data.payment.unsettledSince === null ? null : (
                              <span dir="ltr">{data.payment.unsettledSince}</span>
                            ),
                          absentHe: t('billing.nothingUnsettled'),
                        },
                        {
                          labelHe: t('billing.attempts'),
                          value:
                            data.payment.attempts === 0 ? null : (
                              <span dir="ltr">{data.payment.attempts}</span>
                            ),
                          absentHe: t('billing.noAttempts'),
                        },
                        {
                          labelHe: t('billing.nextCharge'),
                          value:
                            data.nextCharge.scheduled === true ? (
                              <span dir="ltr">{data.nextCharge.on}</span>
                            ) : null,
                          absentHe: t('billing.noNextCharge'),
                        },
                        ]}
                      />
                    </>
                  )}
                </ServerRead>

                {/*
                  ── ITS OWN READ, DELIBERATELY. **2026-11-03, item 3.** ────────────────────
                  An estimate that could not be computed must not blank the recorded facts above
                  it, and a recorded amount must not sit in the same payload as a projection. The
                  panel carries its own `<ServerRead>` for exactly that: the two answers fail
                  independently because they are two different kinds of claim.
                */}
                <NextChargeEstimatePanel />

                <ServerRead result={invoices.result} whatHe={t('billing.readingDocuments')} skeletonRows={5}>
                  {(data) => <InvoiceTable lines={data.invoices} />}
                </ServerRead>
              </>
            ),
          },
          {
            /*
             * ── THE THIRD TAB, 2026-11-01. *"החלפת כרטיס."* ──────────────────────────
             *
             * BETWEEN the documents and the cancellation, in the order a person needs them: what
             * was charged, what it is charged to, and ending it. The panel is a component rather
             * than a third local function because it owns two reads and a write of its own, and
             * this screen is already 565 lines of two tabs.
             *
             * GATED BY THE SERVER, like its neighbours: the card is read and written under
             * `billing.payment_method.update` and the charge list under `billing.invoice.read`.
             * §11's three authorities stay three, and a person holding one of them sees the gate's
             * own refusal inside the half they cannot use.
             */
            id: 'card',
            labelHe: t('card.tab'),
            render: () => <PaymentMethodPanel />,
          },
          {
            /*
             * 0047. THE PATH FOR WHAT THIS SCREEN CANNOT DO. A tab rather than a line under
             * the documents, because it is a correspondence with its own history — and an
             * office on agreed terms uses it instead of the self-serve controls, not after
             * failing with them.
             */
            id: 'change',
            labelHe: t('change.tab'),
            render: () => <ChangeRequestPanel />,
          },
          {
            /*
             * ── THE FOURTH TAB, 2026-11-02. *"צריך גם הוספת מודול."* ─────────────────────
             *
             * BEFORE the cancellation and after the card, which is the order of the question a
             * person is asking on this screen: what was charged, what it is charged to, what else
             * I could take, and ending it. Putting *"add"* after *"cancel"* would read as an
             * afterthought beside the one irreversible control on the screen.
             *
             * THE AUTHORITY IS ITS OWN. Reading the shop is `billing.plan.read`; buying is
             * `billing.module.purchase`, which 0044 grants to the owner role alone — an office
             * member who may read an invoice must not be able to commit the office to a recurring
             * charge. §11's separate authorities become four, and a person holding one and not the
             * other gets the gate's own refusal inside the tab, as its three neighbours do.
             */
            id: 'store',
            labelHe: t('store.tab'),
            render: () => <ModuleStorePanel />,
          },
          {
            id: 'cancellation',
            labelHe: t('billing.cancelTab'),
            render: () => (
              <ServerRead
                result={cancellation.result}
                whatHe={t('billing.readingCancellation')}
                skeletonRows={3}
                /* 404 is `no_subscription` — a state an office can be in, not an error. */
                renderNotFound={() => (
                  <StateBlock
                    titleHe={t('billing.noSubscriptionTitle')}
                    bodyHe={t('billing.noSubscriptionBody')}
                    tone="unavailable"
                  />
                )}
              >
                {(preview) =>
                  cancelled === null ? (
                    <CancellationPanel
                      preview={preview}
                      onDone={cancellation.reload}
                      onCancelled={setCancelled}
                    />
                  ) : (
                    <CancellationDone outcome={cancelled} />
                  )
                }
              </ServerRead>
            ),
          },
        ]}
      />
    </AppShell>
  );
}

/**
 * THE COMPACT TABLE §11 ASKS FOR, and the state column is the point of it.
 *
 * *"אם מסמך טרם הופק או שספק המסמכים נכשל, הצג את המצב האמיתי; אין להציג מסמך מדומה."* So a row
 * with no document shows a dash where the number would be and a chip saying what happened — never
 * a blank that reads as *"nothing was charged"*, and never a download button for a document that
 * does not exist.
 */
function InvoiceTable({ lines }: { readonly lines: readonly InvoiceLineRead[] }): JSX.Element {
  const { t } = useLocale();

  return (
    <DataTable
      captionHe={t('billing.documentsCaption')}
      emptyStateHe={t('billing.documentsEmpty')}
      rows={lines}
      rowKey={(row) => row.documentUid}
      columns={[
        {
          key: 'chargedOn',
          headerHe: t('billing.colDate'),
          render: (row) => <span dir="ltr">{row.chargedOn.slice(0, 10)}</span>,
        },
        {
          key: 'documentNumber',
          headerHe: t('billing.colNumber'),
          /* An em-dash, which is visually distinct from a zero or a blank. 09 §9.2. */
          render: (row) => <span dir="ltr">{row.documentNumber ?? '—'}</span>,
        },
        {
          key: 'period',
          headerHe: t('billing.colPeriod'),
          render: (row) =>
            row.periodStart === null || row.periodEnd === null ? (
              <span>—</span>
            ) : (
              <span dir="ltr">
                {row.periodStart} — {row.periodEnd}
              </span>
            ),
        },
        {
          key: 'amountTotal',
          headerHe: t('billing.colTotal'),
          numeric: true,
          /* THE SERVER'S OWN STRING. See the module header for why this is not re-formatted. */
          render: (row) => <span dir="ltr">{row.amountTotalDisplay ?? '—'}</span>,
        },
        {
          key: 'status',
          headerHe: t('billing.colStatus'),
          render: (row) => <DocumentStatusChip status={row.status} />,
        },
        {
          key: 'view',
          headerHe: t('billing.colView'),
          render: (row) =>
            row.downloadable ? (
              /*
               * ── IT IS A CONTROL NOW, AND IT WAS A WORD BEFORE. 2026-11-01. ──────────────────
               *
               * *"עם צפייה והורדה של המסמך המקורי."* This cell read `זמין לצפייה` — true, and
               * useless: the document was available and nothing could ask for it, because
               * `billing.invoice.download` had no route. It does now.
               *
               * THE ADDRESS IS READ ON THE CLICK AND NOT WITH THE LIST. One list can hold two
               * hundred rows, and asking for two hundred provider addresses in advance to populate
               * links most of which nobody opens would be two hundred calls and a rate limit. It
               * is also
               * why the address is not cached: it is the provider's, and theirs to expire.
               */
              <DocumentLink documentUid={row.documentUid} />
            ) : (
              /*
               * NOT A DISABLED BUTTON. A control that is present and refuses is a control a person
               * clicks and learns nothing from; the words say what the state is, and the chip beside
               * it says why.
               */
              <span className="cell-null">{t('billing.notAvailableYet')}</span>
            ),
        },
      ]}
    />
  );
}


/**
 * THE ORIGINAL DOCUMENT'S LINK. Fetched on the click, opened in a new tab, never stored.
 *
 * ── `window.open` AND NOT `leaveTo` ────────────────────────────────────────────────────────────
 *
 * The payment page REPLACES this application — the customer is going somewhere to do something and
 * coming back. A tax document is a thing to look at beside the list they were reading, and taking
 * their billing screen away to show it would lose their place in a table they are reconciling.
 *
 * `noopener` IS ON THE OPEN. The address is the provider's; a new tab opened without it can reach
 * back into this one through `window.opener`.
 */
function DocumentLink({ documentUid }: { readonly documentUid: string }): JSX.Element {
  const { t } = useLocale();
  const [state, setState] = useState<'idle' | 'fetching' | { failure: ActionFailure }>('idle');

  /*
   * NOT NAMED `fetchAndOpen`, AND THE RENAME IS NOT COSMETIC. `web-client-choke-point` scans this
   * application for the token `fetch` and the first draft of this function tripped it — the check
   * is a TEXT scan, deliberately, because a module that can address any URL it builds a string for
   * is how a sign-in shipped posting to a route that did not exist. A scan that could be talked
   * out of a match by an identifier is a scan with a hole in it, so the identifier moved.
   */
  function openDocument(): void {
    setState('fetching');
    void callOperation('tenant.invoice.download', { params: { document_uid: documentUid } })
      .then((answer) => {
        setState('idle');
        window.open(answer.url, '_blank', 'noopener');
      })
      .catch((error: unknown) => {
        setState({ failure: classifyActionError(error) });
      });
  }

  if (typeof state === 'object') {
    /*
     * THE SERVER'S OWN SENTENCE, IN THE CELL. Three different facts reach here — the document was
     * never issued, it has no provider identity, or the provider refused — and the 422 carries
     * which. A reference code in a table cell tells a person nothing they can act on.
     */
    return <ActionFailureNotice failure={state.failure} />;
  }

  return (
    <button
      type="button"
      className="btn btn--nav"
      disabled={state === 'fetching'}
      onClick={openDocument}
    >
      {t('billing.colView')}
    </button>
  );
}

/**
 * THE CONFIRMATION, AND THE DATE IS THE WHOLE OF IT.
 *
 * *"לאחר הצלחה הצג אישור עם מועד סיום הגישה."* A confirmation that says only *"done"* leaves the
 * person to guess whether their access stopped just now — and it did not.
 */
function CancellationDone({ outcome }: { readonly outcome: CancelledOutcome }): JSX.Element {
  const { t } = useLocale();
  return (
    <StateBlock
      titleHe={t('billing.cancelDoneTitle')}
      bodyHe={t('billing.cancelDoneBody')}
      nextStep={
        <>
          <FactList
            facts={[
              { labelHe: t('billing.accessEnds'), value: <span dir="ltr">{outcome.accessEndsOn}</span> },
            ]}
          />
          {outcome.commentTruncated ? <p>{t('billing.commentTruncated')}</p> : null}
        </>
      }
    />
  );
}

/** The four statuses `CK_billing_document_status` admits, each with its own words. */
function DocumentStatusChip({ status }: { readonly status: string }): JSX.Element {
  const { t } = useLocale();
  if (status === 'issued') return <StatusChip labelHe={t('billing.statusIssued')} tone="ok" />;
  if (status === 'pending_issue') {
    return <StatusChip labelHe={t('billing.statusPending')} tone="muted" />;
  }
  if (status === 'provider_failed') {
    return <StatusChip labelHe={t('billing.statusFailed')} tone="warn" />;
  }
  if (status === 'void') return <StatusChip labelHe={t('billing.statusVoid')} tone="muted" />;
  /*
   * A VALUE NOBODY TAUGHT THIS SCREEN ABOUT IS SHOWN AS ITSELF, not hidden and not mapped to the
   * nearest familiar one. A status this client does not know is a deployment where the server is
   * ahead of it, and printing the raw word is the only honest thing to do.
   */
  return <StatusChip labelHe={status} tone="muted" />;
}

/**
 * §12's CANCELLATION, and every requirement in it is a visible thing on this panel.
 *
 * THE END DATE AND THE CHARGE IMPACT ARE READ FIRST, from the server, and shown above the form. A
 * person cannot reach the confirmation field without the end date being on the screen — which is
 * what *"לא בלחיצה אחת"* means once it is written as a contract rather than as a modal.
 */
function CancellationPanel({
  preview,
  onDone,
  onCancelled,
}: {
  readonly preview: CancellationPreviewRead;
  readonly onDone: () => void;
  readonly onCancelled: (outcome: CancelledOutcome) => void;
}): JSX.Element {
  const { t } = useLocale();
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<CancelOutcome | null>(null);

  /*
   * ALREADY CANCELLED ⇒ THE STATE, NOT THE FORM.
   *
   * *"בכניסה הבאה יוצג שהחידוש בוטל."* The server answers `alreadyCancelled` from the subscription
   * row, so this survives a reload, a new session and a different device — it is not a flag this
   * component remembered.
   */
  if (preview.alreadyCancelled) {
    return (
      <StateBlock
        titleHe={t('billing.cancelAlreadyTitle')}
        bodyHe={t('billing.cancelAlreadyBody')}
        tone="unavailable"
        nextStep={
          <FactList
            facts={[{ labelHe: t('billing.accessEnds'), value: <span dir="ltr">{preview.accessEndsOn}</span> }]}
          />
        }
      />
    );
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    void (async () => {
      try {
        const result = await callOperation('tenant.subscription.cancel', {
          body: { confirmation, reason, comment: comment === '' ? null : comment },
        });
        /*
         * UPWARD FIRST, THEN THE RE-READ. `onDone` unmounts this component — `<ServerRead>` returns
         * to its loading state — so anything recorded in local state here would be gone before it
         * could be shown. The screen holds the confirmation; this only reports it.
         */
        onCancelled({
          accessEndsOn: result.accessEndsOn,
          commentTruncated: result.commentTruncated,
        });
        /* And the preview is re-read, so `alreadyCancelled` becomes the server's answer rather than
           a flag this screen remembered. */
        onDone();
      } catch (err: unknown) {
        /*
         * A 400 OR 409 FROM THIS ROUTE IS A NAMED REFUSAL AND NOT A FAULT. The service answers one
         * of six — `confirmation_mismatch`, `reason_required`, `unknown_reason`,
         * `already_cancelled`, `state_not_cancellable`, `no_subscription` — and the route splits
         * them by what the person does next: 400 when they retype something, 409 when the state is
         * not theirs to retype.
         *
         * THE CODE IS NARROWED TO ONE THIS SCREEN RECOGNISES. A refusal nobody taught this screen
         * about falls through to the uniform wording rather than being rendered, which is the
         * direction `BadRequestError`'s own header requires a disclosure mistake to fail in.
         */
        const refusal = namedRefusal(err);
        if (refusal !== null) {
          setOutcome({ kind: 'refused', reason: refusal });
        } else {
          setOutcome({ kind: 'failed', failure: classifyActionError(err) });
        }
      } finally {
        setSending(false);
      }
    })();
  }

  const typedCorrectly = confirmation.trim() === CONFIRMATION_WORD;
  /*
   * ── THE SERVER'S LIST, OR THIS BUILD'S, AND NEVER A CRASH. 2026-10-30. ───────────────
   *
   * `preview.reasons.map(...)` is what this used to be, and it took the WHOLE APP BLANK the first
   * time the body it was handed did not carry the field — measured in the rendered harness, where
   * a prefix collision answered the cancellation preview with a subscription summary. The DOM
   * suite was green throughout: it renders this screen in its loading state and never reaches the
   * panel.
   *
   * The server's list still wins when it sends one, which is what lets a sixth reason appear before
   * this screen is redeployed. Absent, the five this build has Hebrew for are offered — a form with
   * no choices would be a required field nobody can satisfy.
   */
  const reasons = preview.reasons ?? Object.keys(REASON_KEYS);

  return (
    <>
      {/* WHAT IT MEANS, BEFORE THE FORM. §12's *"הצג את מועד הסיום ואת השפעת החיוב"*. */}
      <FactList
        facts={[
          { labelHe: t('billing.accessEnds'), value: <span dir="ltr">{preview.accessEndsOn}</span> },
          {
            labelHe: t('billing.chargeImpact'),
            value: preview.furtherChargeExpected
              ? t('billing.chargeOutstanding')
              : t('billing.chargeNone'),
          },
        ]}
      />
      <p>{t('billing.cancelExplains')}</p>

      <form onSubmit={submit}>
        <fieldset>
          <legend>{t('billing.reasonLegend')}</legend>
          {/*
            RADIOS AND NOT A DROPDOWN, per §1: *"בחירה בין חלופות תהיה בכפתורי רדיו"*. Five
            mutually exclusive answers, all visible, so choosing one is one click and reading them
            is not a menu.
          */}
          {reasons.map((code) => (
            <label key={code} className="field field--choice">
              <input
                type="radio"
                name="cancel-reason"
                value={code}
                checked={reason === code}
                onChange={() => setReason(code)}
              />
              <span>{reasonLabel(t, code)}</span>
            </label>
          ))}
        </fieldset>

        <label className="field">
          <span className="field__label">{t('billing.commentLabel')}</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            /* OPTIONAL, and it says so. §12: *"טקסט חופשי לא חובה"*. */
            placeholder={t('billing.commentOptional')}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('billing.confirmLabel')}</span>
          <input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            /*
              `spellCheck` and autocapitalise OFF. A keyboard that helpfully capitalises the first
              letter and lower-cases the rest would type `Cancel`, which is not the word — and the
              person would be told their confirmation was wrong by their own phone.
            */
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby="cancel-word"
          />
          <span id="cancel-word" className="field__hint">
            {t('billing.confirmHint')}
          </span>
        </label>

        {/*
          THE BUTTON IS DISABLED UNTIL BOTH ARE GIVEN, and that is a convenience and NOT the
          control: the server checks the state, the word and the reason again, in that order, and
          the capability's own `per_action` step-up is demanded by the gate before the handler runs.
          *"הקלדת `CANCEL` אינה תחליף לאבטחה."*
        */}
        <button type="submit" className="btn btn--danger" disabled={sending || !typedCorrectly || reason === ''}>
          {sending ? t('billing.cancelSending') : t('billing.cancelSubmit')}
        </button>
      </form>

      {outcome?.kind === 'refused' ? (
        <StateBlock
          titleHe={t('billing.cancelRefusedTitle')}
          bodyHe={refusalText(t, outcome.reason)}
          tone="error"
        />
      ) : null}
      {outcome?.kind === 'failed' ? (
        <ActionFailureNotice failure={outcome.failure} onStepUpSatisfied={onDone} />
      ) : null}
    </>
  );
}
